// Shared plan persistence for the native editor.
//
// The ENTIRE plan is kept in localStorage under one key, shared by ~15
// section controllers (plan_store, wizard, client_section,
// resource_calendar, dynamic_list, plan_modes, …). Each controller keeps
// its OWN in-memory copy of the plan, captured at its connect() time.
//
// THE DATA-LOSS BUG this module fixes:
// Historically every controller saved by blindly serializing its WHOLE
// private copy over the shared key (localStorage.setItem(KEY, JSON(this.plan))).
// Because most controllers only refresh their copy for a small whitelist
// of changes (and some never refresh), a controller's copy goes stale —
// missing everything typed in OTHER sections. One action in that section
// then wrote its truncated copy over storage and WIPED the rest. The
// legacy iframe editor had ONE shared plan object mutated in place, so no
// second copy could clobber it — which is why this only happened here.
//
// THE FIX: savePlan() is a read-MODIFY-write MERGE. It reads the current
// stored plan and overlays only the caller's keys, so a controller can
// add/update its own data but can NEVER erase keys owned by other
// sections. Blind whole-plan replacement is available as replacePlan()
// for the few flows that legitimately replace everything (opening a .sdp,
// restoring a revision, starting a new plan).
//
// It also surfaces any write failure (quota / private-mode / serialize
// error) with a visible warning instead of the old silent `catch (_) {}`,
// and downscaleImageDataUrl() keeps uploaded photos small.

import { perfTime } from "perf"
import { mirrorToIdb, readIdbBackup } from "plan_backup"

export const STORAGE_KEY = "pcp_plan_data"

// Read the current stored plan (or {} if absent/corrupt). On a corrupt (non-
// empty but unparseable) value, preserve the raw bytes under a side key FIRST
// so a later save can't overwrite recoverable data.
export function loadPlan() {
  let raw = null
  try { raw = localStorage.getItem(STORAGE_KEY) } catch (_) { return {} }
  if (!raw) return {}
  try {
    return JSON.parse(raw) || {}
  } catch (e) {
    try {
      if (!localStorage.getItem(STORAGE_KEY + ".corrupt")) {
        localStorage.setItem(STORAGE_KEY + ".corrupt", raw)
      }
    } catch (_) { /* quota/blocked — nothing more we can do */ }
    console.error("[plan] stored plan is corrupt — preserved a copy under " + STORAGE_KEY + ".corrupt", e)
    return {}
  }
}

// Boot recovery: if localStorage has no usable plan (evicted by ITP / cleared /
// corrupt) but the IndexedDB mirror has a backup, restore it to localStorage
// and return it so the editor can re-hydrate. Returns null when there's a live
// plan already (never overwrites it) or no backup exists.
export async function restoreFromIdbIfNeeded() {
  let raw = null
  try { raw = localStorage.getItem(STORAGE_KEY) } catch (_) {}
  if (raw) {
    try { const p = JSON.parse(raw); if (p && Object.keys(p).length) return null } catch (_) { /* corrupt — fall through to restore */ }
  }
  const backup = await readIdbBackup()
  if (!backup) return null
  let plan
  try { plan = JSON.parse(backup) } catch (_) { return null }
  if (!plan || typeof plan !== "object" || !Object.keys(plan).length) return null
  try { localStorage.setItem(STORAGE_KEY, backup) } catch (_) {}
  return plan
}

// Effective "is this section included in the plan output?" decision, shared by
// the section chips (Include/Exclude), the preview/PDF renderer, the TOC and
// the nav rail so they never disagree. An explicit user toggle
// (meta.sectionEnabled[key] === true/false) always wins. With no explicit
// choice, sections default ON — EXCEPT the Behavior Communication Chart
// ("communication"), which defaults OFF unless the participant's communication
// styles include "Behavioral Communication" (plan.about.communicationStyle).
export function isSectionIncluded(plan, key) {
  const se = plan && plan.meta ? plan.meta.sectionEnabled : null
  const explicit = se ? se[key] : undefined
  if (explicit === true)  return true
  if (explicit === false) return false
  if (key === "communication") {
    const styles = plan && plan.about ? plan.about.communicationStyle : null
    // communicationStyle rows are {id, text} objects (dynamic-list / wizard
    // multi-select), though older/imported data may hold plain strings — read
    // the text off either shape before matching.
    return Array.isArray(styles) && styles.some(s => {
      const t = (s && typeof s === "object") ? (s.text != null ? s.text : "") : s
      return String(t == null ? "" : t).trim().toLowerCase() === "behavioral communication"
    })
  }
  return true
}

// Effective "the participant does not attend school" decision, shared by the
// Day-in-Life editor (dil-toggles) and the preview/PDF so they agree. True if
// EITHER the Day-in-Life "Does not go to school" flag is set OR the
// participant's School Details (about.schoolDetails) include a "Does not go to
// school" entry — so setting it in either place collapses the weekday routine
// to the weekend structure (School Time hidden, After School → Home &
// Community). schoolDetails rows are {id, text} objects (dynamic-list), though
// older/imported data may hold plain strings — read the text off either shape.
export function isNotAttendingSchool(plan) {
  if (plan && plan.dayInLife && plan.dayInLife.notAttendingSchool === true) return true
  const sd = plan && plan.about ? plan.about.schoolDetails : null
  return Array.isArray(sd) && sd.some(s => {
    const t = (s && typeof s === "object") ? (s.text != null ? s.text : "") : s
    return String(t == null ? "" : t).trim().toLowerCase() === "does not go to school"
  })
}

// Canonical display NAME of a goal/barrier resource — the linking key for
// Current Services. A custom ("Other") service keeps its real name in
// r.comment; every other service is keyed by its type. Keying by NAME (not the
// literal "Other") is what stops two differently-named custom services — e.g.
// "Adaptive Sports" and "Adaptive Camp" — from collapsing into one linked row.
export function serviceName(resource) {
  if (!resource || typeof resource !== "object") return ""
  const type = String(resource.type || resource.text || "").trim()
  if (type === "Other") {
    const name = String(resource.comment || "").trim()
    return name || "Other"
  }
  return type
}

// Does resource `r` belong to the Current Services link keyed by `key`
// (a serviceName)? Backward-compat: a legacy item still keyed by the literal
// "Other" matches every "Other"-typed resource (the old merged behavior) until
// migrateCurrentServicesLinks() splits it into per-name links.
export function matchesServiceLink(r, key) {
  if (!r) return false
  if (serviceName(r) === key) return true
  if (key === "Other" && String(r.type || r.text || "").trim() === "Other") return true
  return false
}

// One-time migration: a legacy Current Services item keyed by the literal
// "Other" merged ALL custom services into one row. Split it into one link per
// distinct NAMED "Other" service found in goals/barriers. No-op unless there is
// an "Other" item AND at least one named custom service. Mutates + returns true
// when it changes plan.currentServices; the caller persists.
export function migrateCurrentServicesLinks(plan) {
  if (!plan || !Array.isArray(plan.currentServices)) return false
  const cs = plan.currentServices
  if (!cs.some(s => s && s.linkedType === "Other")) return false
  const names = []
  const seen = new Set()
  const scan = (arr) => (Array.isArray(arr) ? arr : []).forEach(g =>
    (g && Array.isArray(g.resources) ? g.resources : []).forEach(r => {
      if (!r || r.tag === "unmet") return
      if (String(r.type || r.text || "").trim() !== "Other") return
      const n = String(r.comment || "").trim()
      if (n && n !== "Other" && !seen.has(n)) { seen.add(n); names.push(n) }
    }))
  scan(plan.goals)
  scan(plan.barriers)
  if (names.length === 0) return false
  const existing = new Set(cs.filter(s => s && s.linkedType && s.linkedType !== "Other").map(s => s.linkedType))
  const out = []
  let seeded = false
  cs.forEach(s => {
    if (s && s.linkedType === "Other") {
      if (!seeded) {
        seeded = true
        names.forEach(n => {
          if (existing.has(n)) return
          existing.add(n)
          out.push({ id: Date.now() + Math.random(), text: n, linkedType: n, tag: s.tag === "rc" ? "rc" : "general" })
        })
      }
      // legacy "Other" item dropped — replaced by the named links above
    } else {
      out.push(s)
    }
  })
  plan.currentServices = out
  return true
}

// Recursive merge: `over` wins. Plain objects are merged key-by-key so
// keys present only in `base` survive; arrays and primitives are taken
// wholesale from `over` (the owning controller is authoritative for its
// own lists). `undefined` in `over` does NOT erase a base value.
export function deepMerge(base, over) {
  if (over === undefined) return base
  if (over === null || typeof over !== "object" || Array.isArray(over)) return over
  const out = (base && typeof base === "object" && !Array.isArray(base)) ? { ...base } : {}
  for (const k of Object.keys(over)) out[k] = deepMerge(out[k], over[k])
  return out
}

// Incremental save: MERGE the caller's plan onto whatever is stored now,
// then persist. A controller holding a stale/partial copy can only add or
// update its own keys — it can never wipe data written by other sections.
// Returns true on success, false if the browser refused the write.
export function savePlan(plan) {
  const merged = perfTime("plan.savePlan(merge)", () => deepMerge(loadPlan(), plan))
  return _write(merged)
}

// Full replacement: persist `plan` verbatim, discarding whatever is
// stored. ONLY for flows that intentionally replace the whole plan —
// opening an encrypted .sdp file, restoring a saved revision, or starting
// a brand-new/blank plan. Everyday edits must use savePlan() (merge).
export function replacePlan(plan) {
  return _write(plan)
}

// Coalesce a burst of rapid calls (e.g. keystrokes) into a single trailing
// call after `wait` ms of quiet. The returned function carries:
//   .flush()  — run the pending call NOW (call from disconnect() so a
//               navigation/blur never drops the last edit)
//   .cancel() — drop any pending call
// The in-memory model is updated immediately by the caller BEFORE the
// debounced save, so only the persist + broadcast is delayed — no data is
// lost between keystrokes.
// ----- Unsaved-changes (dirty) tracking ---------------------------------
// A save-debounce is "pending" from the moment an edit schedules it until it
// writes. The app is DIRTY while any save-debounce is pending. Save-debounces
// opt in via debounce(fn, wait, { dirty: true }); a generic debounce (e.g. a
// debounced re-render) does NOT, so the flag never lies. UI (the per-goal Save
// button) subscribes via onDirtyChange.
const _pendingSaves = new Set()
const _dirtyListeners = new Set()
function _notifyDirty() {
  const d = _pendingSaves.size > 0
  _dirtyListeners.forEach(fn => { try { fn(d) } catch (_) { /* isolate listeners */ } })
}
export function isDirty() { return _pendingSaves.size > 0 }
export function onDirtyChange(fn) { _dirtyListeners.add(fn); return () => _dirtyListeners.delete(fn) }

export function debounce(fn, wait = 200, opts = {}) {
  const tracks = !!opts.dirty
  let timer = null
  let pending = null
  const clean = () => { if (tracks && _pendingSaves.delete(debounced)) _notifyDirty() }
  const run = () => {
    timer = null
    const args = pending; pending = null
    try { if (args) fn(...args) } finally { clean() }
  }
  const debounced = (...args) => {
    pending = args
    if (tracks && !_pendingSaves.has(debounced)) { _pendingSaves.add(debounced); _notifyDirty() }
    if (timer) clearTimeout(timer)
    timer = setTimeout(run, wait)
  }
  debounced.flush  = () => { if (timer) { clearTimeout(timer); run() } }
  debounced.cancel = () => { if (timer) { clearTimeout(timer); timer = null } pending = null; clean() }
  return debounced
}

// ----- Last-chance flush on tab close / hide -----------------------------
// Controllers with debounced/scheduled saves register a flusher here. On
// pagehide (the reliable close + bfcache hook — beforeunload is unreliable on
// mobile) and on visibilitychange→hidden (covers tab-switch / app-background /
// the OS discarding a backgrounded tab), we synchronously run every pending
// save so the last edit is never lost inside a debounce window. Flushers must
// be side-effect-safe to call when nothing is pending (guard internally).
const _flushers = new Set()
export function registerFlush(fn) {
  _flushers.add(fn)
  return () => _flushers.delete(fn)
}
function _flushAll() {
  _flushers.forEach(fn => { try { fn() } catch (_) { /* one flusher must not block the rest */ } })
}
// Public flush — run EVERY pending save NOW. Call before any action that
// reloads the plan from storage and rewrites an array wholesale (e.g. adding a
// goal), so an in-flight edit in another section's debounce is written first
// and not clobbered. Also backs the per-goal Save button.
export function flushAll() { _flushAll() }
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", _flushAll)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _flushAll()
  })
  // Courtesy warning: if the user closes/navigates with an edit still inside a
  // debounce window, prompt before leaving. (pagehide still flushes, so data is
  // saved either way — this is the explicit "you haven't saved" heads-up.)
  window.addEventListener("beforeunload", (e) => {
    if (isDirty()) { e.preventDefault(); e.returnValue = "" }
  })
}

// ----- Single-tab write gate --------------------------------------------
// The plan is ONE shared localStorage blob; two tabs editing it can clobber
// each other. The tab-lock controller elects a single writer tab and flips
// this gate. A non-writer tab must NOT persist (a diverging blob would
// overwrite the active tab) — _write() refuses while blocked. This is the
// real data-loss guarantee behind the "open in another tab" overlay.
let _writable = true
export function setWritable(b) { _writable = !!b }
export function isWritable() { return _writable }

function _write(plan) {
  // Blocked (another tab owns the editing session): refuse silently rather
  // than clobber it. The overlay prevents the user from editing here anyway.
  if (!_writable) return false
  let json
  try {
    json = JSON.stringify(plan)
  } catch (e) {
    // Serialization itself failed (e.g. a circular reference or a
    // non-serializable value got into the plan) — every save would throw
    // here, so NOTHING would ever persist. Surface it instead of hiding it.
    console.error("[plan] could not serialize plan for save:", e)
    showSaveErrorToast("⚠ Your changes can’t be saved — internal error. Please export/copy your work and reload.")
    return false
  }
  try {
    localStorage.setItem(STORAGE_KEY, json)
    _hideSaveErrorBanner()   // a successful write clears any prior failure banner
    mirrorToIdb(json)        // durable write-behind backup (best-effort, async)
    return true
  } catch (e) {
    // Any write failure means data is NOT being saved. Never swallow it:
    //   - QuotaExceededError → storage full (usually an oversized photo)
    //   - SecurityError / private-mode / storage disabled → blocked
    console.error("[plan] localStorage write failed:", e)
    if (isQuotaError(e)) {
      showSaveErrorToast(
        "⚠ Storage is full — your recent changes may not be saved. " +
        "A photo is likely too large; remove or re-add it to shrink the plan."
      )
    } else {
      showSaveErrorToast(
        "⚠ Your changes are NOT being saved (browser storage is blocked — " +
        "private/incognito mode or site data disabled). Use a normal window, " +
        "or Save an encrypted .sdp file to keep your work."
      )
    }
    return false
  }
}

function isQuotaError(e) {
  if (!e) return false
  return (
    e.name === "QuotaExceededError" ||
    e.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    e.code === 22 ||
    e.code === 1014
  )
}

// A data-loss warning must NOT fade like an ordinary toast — the user could
// keep typing into a plan that isn't being saved. Use a dedicated STICKY top
// banner (its own element, so transient "Saved" toasts can't clobber it) that
// stays until a save actually succeeds (_hideSaveErrorBanner, from _write).
function showSaveErrorToast(msg) {
  let bar = document.getElementById("pcp-save-error-banner")
  if (!bar) {
    bar = document.createElement("div")
    bar.id = "pcp-save-error-banner"
    bar.setAttribute("role", "alert")
    bar.style.cssText =
      "position:fixed;top:0;left:0;right:0;z-index:2147483646;padding:12px 18px;" +
      "background:#fee2e2;color:#991b1b;font:600 13px/1.45 ui-sans-serif,system-ui;" +
      "text-align:center;box-shadow:0 2px 12px rgba(0,0,0,.18);"
    document.body.appendChild(bar)
  }
  bar.textContent = msg
  bar.style.display = "block"
}
function _hideSaveErrorBanner() {
  const bar = document.getElementById("pcp-save-error-banner")
  if (bar) bar.style.display = "none"
}

// Downscale an image data URL so its longest side is <= maxDim and
// re-encode it as JPEG at `quality`. Keeps stored photos to ~tens of KB
// so they don't blow the localStorage budget the whole plan shares.
// Resolves to the (smaller) data URL, or rejects if the image can't be
// decoded (e.g. an unsupported HEIC on desktop Chrome).
export function downscaleImageDataUrl(dataUrl, maxDim = 800, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      let width = img.width
      let height = img.height
      if (width > height) {
        if (width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim }
      } else {
        if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim }
      }
      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext("2d")
      if (!ctx) { reject(new Error("no 2d context")); return }
      ctx.drawImage(img, 0, 0, width, height)
      try { resolve(canvas.toDataURL("image/jpeg", quality)) }
      catch (e) { reject(e) }
    }
    img.onerror = () => reject(new Error("image decode failed"))
    img.src = dataUrl
  })
}

// Bytes represented by a data URL's base64 payload (~length * 3/4).
export function dataUrlBytes(dataUrl) {
  const i = String(dataUrl || "").indexOf(",")
  if (i < 0) return 0
  return Math.ceil((dataUrl.length - i - 1) * 0.75)
}

function _scaledCanvas(src, w, h) {
  const c = document.createElement("canvas")
  c.width = Math.max(1, Math.round(w))
  c.height = Math.max(1, Math.round(h))
  c.getContext("2d").drawImage(src, 0, 0, c.width, c.height)
  return c
}

// Encode a canvas to a JPEG data URL guaranteed under maxBytes: cap the
// longest side to maxDim, then step JPEG quality down, then shrink the
// dimensions, until it fits. Used so cropped photos stay under ~1 MB and
// never overflow the shared localStorage budget.
export function compressCanvasToTarget(canvas, maxBytes = 1_000_000, maxDim = 1400) {
  let cv = canvas
  const longest = Math.max(cv.width, cv.height)
  if (longest > maxDim) cv = _scaledCanvas(cv, cv.width * (maxDim / longest), cv.height * (maxDim / longest))
  let quality = 0.9
  let out = cv.toDataURL("image/jpeg", quality)
  while (dataUrlBytes(out) > maxBytes && quality > 0.4) {
    quality -= 0.1
    out = cv.toDataURL("image/jpeg", quality)
  }
  while (dataUrlBytes(out) > maxBytes && Math.max(cv.width, cv.height) > 400) {
    cv = _scaledCanvas(cv, cv.width * 0.85, cv.height * 0.85)
    out = cv.toDataURL("image/jpeg", 0.8)
  }
  return out
}
